import React from 'react';
import { useMutation } from '@apollo/client';
import { Button, Input, Select, message, InputNumber } from 'antd';
import uniqueId from 'lodash/uniqueId';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TransferError from './TransferError';
import { ReferralRemove as ReferralRemoveIcon } from 'common/components/svgs';
import { PartnerPayoutRecordStage, LandlordCommissionCurrencies } from '~constants/referral';
import {
  MUTATION_BULK_TRANSFER_TO_PARTNER,
  QUERY_PARTNER_TRANSACTION,
} from '~pages/dashboard/referral/transfer/queries';
import TransferPreview from '../transfer-preview';
import useTransferValidate from './hooks';

const defaultFormItem = () => ({
  id: uniqueId(),
  partnerCode: '',
  bookingNumber: '',
  payoutStage: undefined,
  currency: undefined,
  transferAmount: '',
  passportFirstName: '',
  passportLastName: '',
});

function TransferEditor({ onClose, handleStartProcess, type = 'upload', defaultTransferForm }) {
  const { t } = useTranslation();
  const { validateTransfer } = useTransferValidate();

  const [showEditForm, setShowEditForm] = useState(type === 'edit');
  const [showPreviewForm, setShowPreviewForm] = useState(type === 'upload');

  const [formData, setFormData] = useState(defaultTransferForm || [defaultFormItem()]);
  const [validateBulkTransferResult, setValidateBulkTransferResult] = useState({});

  const [bulkTransferToPartner] = useMutation(MUTATION_BULK_TRANSFER_TO_PARTNER);

  const onValidate = async data => {
    try {
      // Validation in local
      const { isAllValid: localIsValid, validateResult: localValidateResult } =
        await validateTransfer('form', data);
      if (!localIsValid) {
        setValidateBulkTransferResult(localValidateResult);
        return localIsValid;
      }

      // Validation in backend.
      const { isAllValid: responseIsValid, validateResult: responseValidateResult } =
        await validateTransfer('response', data);
      if (!responseIsValid) {
        setValidateBulkTransferResult(responseValidateResult);
        return responseIsValid;
      }

      // All validation pass and move on.
      setValidateBulkTransferResult({});
      return true;
    } catch (error) {
      setValidateBulkTransferResult({
        unknownError: {
          success: false,
          errors: {
            unknown: {
              errorMessage:
                error.message || error || t('cms.referral.transfer.editor.error.unknown'),
              effectItems: [],
            },
          },
        },
      });
    }
  };

  const handleSubmit = async formData => {
    try {
      const pretreatmentData = formData.map(item => ({
        ...item,
        transferAmount: String(item.transferAmount),
      }));
      const isValid = await onValidate(pretreatmentData);
      if (!isValid) return;
      if (type === 'edit' && showEditForm) {
        setShowEditForm(false);
        setShowPreviewForm(true);
      } else {
        handleConfirmBulkTransfer(pretreatmentData);
      }
    } catch (error) {
      console.log(error);
      console.log(error.message);
      message.error(error.message || error);
    }
  };

  const handleConfirmBulkTransfer = data => {
    const variables = data.map(
      ({
        transferAmount,
        bookingNumber,
        currency,
        partnerCode,
        payoutStage,
        passportFirstName,
        passportLastName,
        S3Path,
      }) => ({
        amount: transferAmount,
        bookingNumber,
        currency,
        partnerCode,
        payoutStage,
        passportFirstName,
        passportLastName,
        ...(S3Path ? { s3Path: S3Path } : {}),
      }),
    );

    const processPromise = () =>
      bulkTransferToPartner({
        variables: { input: variables },
        refetchQueries: [QUERY_PARTNER_TRANSACTION],
      });
    handleStartProcess(processPromise);
    onClose();
  };

  return (
    <>
      <div className="transfer-editor__body">
        <section className="transfer-editor__section">
          <h2 className="transfer-editor__section__title">
            {t('cms.referral.transfer.editor.section.title')}
          </h2>
          <p>{t('cms.referral.transfer.editor.max_limit_add.error')}</p>

          <TransferError error={validateBulkTransferResult} />

          {showEditForm && (
            <div className="transfer-editor__form-container">
              <TransferForm formData={formData} updateFormData={setFormData} />
            </div>
          )}

          {showPreviewForm && (
            <div className="transfer-editor__preview-container">
              <TransferPreview transferData={formData} />
            </div>
          )}
        </section>
      </div>
      <div className="transfer-editor__footer">
        <Button size="large" className="transfer-editor__cancel-button" onClick={onClose}>
          {t('cms.referral.partner.detail.editor.btn.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="transfer-editor__save-button"
          // loading={updateReferrerLoading}
          onClick={() => handleSubmit(formData)}
        >
          {type === 'edit'
            ? t('cms.referral.partner.detail.editor.btn.check')
            : t('cms.referral.partner.detail.editor.btn.save')}
        </Button>
      </div>
    </>
  );
}

function TransferForm({ formData, updateFormData }) {
  const { t } = useTranslation();
  const handleRemoveFormItem = id => {
    if (formData.length > 1) {
      updateFormData(formData => formData.filter(item => item.id !== id));
    } else {
      message.warning(t('cms.referral.transfer.editor.min_require.error'));
    }
  };

  const handleAddFormItem = () => {
    if (formData.length < 20) {
      updateFormData(formData.concat([defaultFormItem()]));
    } else {
      message.warning(t('cms.referral.transfer.editor.max_limit_add.error'));
    }
  };

  const handleUpdateFormItem = (index, key, value) => {
    formData[index][key] = value;
    updateFormData([...formData]);
  };

  return (
    <div>
      <div className="transfer-editor__form__tr">
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-104">
          {t('cms.referral.partner.list.column.partner_code')}
        </div>
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-104">
          {t('cms.referral.payout.list.column.oppos_no')}
        </div>
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-104">
          {t('cms.referral.transfer.list.column.passport_first_name')}
        </div>
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-104">
          {t('cms.referral.transfer.list.column.passport_last_name')}
        </div>
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-112">
          {t('cms.referral.payout.list.column.trigger_point')}
        </div>
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-80">
          {t('cms.referral.transfer.list.column.currency')}
        </div>
        <div className="transfer-editor__form__tr__th transfer-editor__form__tr--w-112">
          {t('cms.referral.transfer.list.column.amount')}
        </div>
      </div>
      <div className="transfer-editor__form__tbody">
        {formData.map((formItem, itemIndex) => (
          <div key={formItem.id} className="transfer-editor__form__tr">
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-104">
              <Input
                value={formItem.partnerCode}
                placeholder={t('cms.referral.transfer.editor.placeholder.partner_code')}
                onChange={e => handleUpdateFormItem(itemIndex, 'partnerCode', e.target.value)}
              />
            </div>
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-104">
              <Input
                value={formItem.bookingNumber}
                placeholder={t('cms.referral.transfer.editor.placeholder.opps')}
                onChange={e => handleUpdateFormItem(itemIndex, 'bookingNumber', e.target.value)}
              />
            </div>
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-104">
              <Input
                value={formItem.passportFirstName}
                placeholder={t('cms.referral.transfer.editor.placeholder.passport_first_name')}
                onChange={e => handleUpdateFormItem(itemIndex, 'passportFirstName', e.target.value)}
              />
            </div>
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-104">
              <Input
                value={formItem.passportLastName}
                placeholder={t('cms.referral.transfer.editor.placeholder.passport_last_name')}
                onChange={e => handleUpdateFormItem(itemIndex, 'passportLastName', e.target.value)}
              />
            </div>
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-112">
              <Select
                className="transfer-editor__w-full"
                value={formItem.payoutStage}
                placeholder={t('cms.referral.transfer.editor.placeholder.payout_stage')}
                onSelect={value => handleUpdateFormItem(itemIndex, 'payoutStage', value)}
              >
                {Object.keys(PartnerPayoutRecordStage).map(key => (
                  <Select.Option key={key} value={key}>
                    {t(
                      `cms.referral.transfer.editor.select.payout_stage.options.${PartnerPayoutRecordStage[
                        key
                      ].toLowerCase()}`,
                    )}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-80">
              <Select
                className="transfer-editor__w-full"
                value={formItem.currency}
                placeholder={t('cms.referral.transfer.editor.placeholder.currency')}
                onSelect={value => handleUpdateFormItem(itemIndex, 'currency', value)}
              >
                {Object.keys(LandlordCommissionCurrencies).map(key => (
                  <Select.Option key={key} value={key}>
                    {LandlordCommissionCurrencies[key]}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="transfer-editor__form__tr__td transfer-editor__form__tr--w-112">
              <InputNumber
                value={formItem.transferAmount}
                placeholder={t('cms.referral.transfer.editor.placeholder.amount')}
                onChange={e => handleUpdateFormItem(itemIndex, 'transferAmount', e)}
              />
            </div>
            <div
              className="transfer-editor__form__tr__td commission-plan-editor__tiers-group--flex commission-plan-editor__tiers-group--cursor-pointer"
              onClick={() => handleRemoveFormItem(formItem.id)}
            >
              <ReferralRemoveIcon />
            </div>
          </div>
        ))}
      </div>
      <button className="transfer-editor__add-new" onClick={handleAddFormItem}>
        {t('cms.referral.transfer.button.add')}
      </button>
    </div>
  );
}

export default TransferEditor;
